/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/header.scss';

import React, {MouseEventHandler, useContext, useState} from 'react';
import {AwesomeIcon, CloseButton, copyToClipboard, getMode} from '@dvrd/dvr-controls';
import {userHasSubscription} from "../../util/utils";
import userCircle from '@iconify/icons-fa-solid/user-circle';
import signOut from '@iconify/icons-fa/sign-out';
import {Icon} from '@iconify/react';
import {AppContext} from '../misc/appContext';
import HeaderCrumbs from "./crumbs/headerCrumbs";
import classNames from 'classnames';
import {UserType} from "../../util/interfaces";
import {hasAdminLogin} from "../../util/adminUtil";
import ContextAccountController from "../account/select/contextAccountController";
import {onShowConfirmSnackbar} from "../../util/renderUtil";
import SystemMessagesController from "../systemMessage/systemMessagesController";
import Image from "../misc/image";
import IDate from '@dvrd/idate';

interface Props {
    onClickLogo: MouseEventHandler;
    onClickUpgrade: MouseEventHandler;
    onClickLogout: MouseEventHandler;
    onClickDemoRegister: MouseEventHandler;
    menuWidth: number;
    isDemo: boolean;
    upgradeClicked: boolean;
}

export default function Header(props: Props) {
    const {onClickLogo, onClickLogout, menuWidth, onClickUpgrade, onClickDemoRegister, isDemo, upgradeClicked} = props,
        context = useContext(AppContext),
        {user} = context.userContext,
        [adminStatsVisible, setAdminStatsVisible] = useState(true);

    function onDoubleClickEnv(evt: React.MouseEvent) {
        const label = evt.target as HTMLSpanElement;
        label.parentElement?.removeChild(label);
    }

    function onClickId(id: string) {
        return function() {
            copyToClipboard(id);
            onShowConfirmSnackbar(`${id} gekopieerd`);
        }
    }

    function onHideAdminStats() {
        setAdminStatsVisible(false);
    }

    function renderEnv() {
        if (isDemo) return renderDemoEnv();
        const mode = getMode();
        let env: string | null = null;
        if (mode === 'dev') env = 'Develop omgeving';
        else if (mode === 'accept') env = 'Acceptatie omgeving';
        else if (['test', 'sub'].includes(mode)) env = 'Test omgeving';
        if (env) return <span className='env' onDoubleClick={onDoubleClickEnv}>{env}</span>;
        return null;
    }

    function renderDemoEnv() {
        return (
            <div className='demo-header'>
                <span className='demo-label'>Demo account</span>
                <div className='button-container'>
                    <div className={classNames('upgrade-btn', 'blue')}
                         onClick={onClickDemoRegister}>
                        <label className='btn-label'>Ja, ik probeer het nu voor 14 dagen gratis</label>
                    </div>
                    <label className='disclaimer'>Stopt automatisch, geen betaalgegevens nodig</label>
                </div>
            </div>
        )
    }

    function renderTrialCounter() {
        const {user} = context.userContext;
        if (!user?.subscription || userHasSubscription(user, false)) return null;
        const {trialEndDate} = user.subscription;
        const now = IDate.now();
        const showDays = trialEndDate?.isAtOrAfter(now, 'day');
        const daysLeft: number = Math.ceil(trialEndDate?.diff(now, 'd', true) ?? 0);
        const countColor = daysLeft < 5 ? 'red' : daysLeft < 10 ? 'orange' : 'green';
        return (
            <div className='trial-counter'>
                {!!showDays && <>
                    <label className='count-label'>Demo nog </label>
                    <span className={classNames('count', countColor)}>{daysLeft}</span>
                    <label className='count-label'> {daysLeft === 1 ? 'dag' : 'dagen'}</label>
                </>}
                <div className={classNames('upgrade-btn', upgradeClicked && 'inactive')} onClick={onClickUpgrade}>
                    <label className='btn-label'>Abonnement starten </label>
                    <AwesomeIcon name='arrow-right'/>
                </div>
            </div>
        )
    }

    function renderAdminStats() {
        const {user} = context.userContext, {account} = context.accountContext;
        if (!user || (!hasAdminLogin() && user.type !== UserType.SUPER_ADMIN) || !adminStatsVisible) return null;
        const apiKey = account?.getAPIKey() ?? null;
        return (
            <div className='admin-stats'>
                <CloseButton onClick={onHideAdminStats} className='hide-button'/>
                <>
                    <label className='key'>Gebruiker:</label>
                    <label onClick={onClickId(user.id)} className='click' title='Kopiëren'>{user.id}</label>
                </>
                {account !== null && (
                    <>
                        <label className='key'>Account:</label>
                        <label onClick={onClickId(account.id)} className='click' title='Kopiëren'>{account.id}</label>
                    </>
                )}
                {apiKey !== null && (
                    <>
                        <label className='key'>API key:</label>
                        <label onClick={onClickId(apiKey.id)} className='click' title='Kopiëren'>{apiKey.id}</label>
                    </>
                )}
            </div>
        )
    }

    return (
        <>
            <div className='bolmate-header'>
                <SystemMessagesController/>
                <div className='header-inner'>
                    <div className='logo-container' style={{width: `${menuWidth}px`}}>
                        <Image name='logo' className='logo' onClick={onClickLogo}/>
                    </div>
                    <div className='header-content right'>
                        <HeaderCrumbs/>
                        <div style={{display: 'flex'}}>
                            {renderEnv()}
                            {renderTrialCounter()}
                            {renderAdminStats()}
                        </div>
                        {user !== null && <div className='user-container'>
                            <ContextAccountController/>
                            <Icon icon={userCircle} className='user-icon'/>
                            <label>{user.fullName}</label>
                            <div className='sign-out-btn' onClick={onClickLogout}>
                                <Icon icon={signOut}/>
                            </div>
                        </div>}
                    </div>
                </div>
            </div>
        </>
    )
}
